/**
 * 日落橙模板 - 单页
 * 
 * 设计风格（落日琥珀色调）：
 * - 琥珀渐变头部 + 金色装饰
 * - 清爽白色内容卡片
 * - 居中阅读区，宽松排版
 * - 暖色系背景
 */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/templates/sunset/components/Navbar";
import SunsetFooter from "@/templates/sunset/components/Footer";
import { getSinglePage } from "@/utils/api";

export default function SunsetSinglePage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getSinglePage(slug)
      .then((data) => setPage(data))
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <LoadingSkeleton />;
  if (!page) return <NotFound />;

  return (
    <div className="min-h-screen bg-[#fffbf5]">
      <Navbar />

      {/* ── 页面头图区域 ── */}
      <section className="relative bg-gradient-to-br from-amber-100 via-orange-50 to-amber-50 pt-24 pb-14 md:pb-20">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 30% 50%, #f59e0b 0%, transparent 50%), radial-gradient(circle at 70% 50%, #d97706 0%, transparent 50%)"
        }} />
        <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            {page.title}
          </h1>
          {/* 装饰横线 */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <span className="w-10 h-0.5 bg-amber-300 rounded-full" />
            <span className="w-2 h-2 bg-amber-500 rounded-full" />
            <span className="w-10 h-0.5 bg-amber-300 rounded-full" />
          </div>
        </div>
      </section>

      {/* ── 面包屑 ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-3 flex items-center gap-1.5 text-sm">
          <Link to="/" className="text-gray-400 hover:text-amber-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-xs text-gray-300" />
          <span className="text-gray-700 font-medium">{page.title}</span>
        </div>
      </div>

      {/* ── 内容区 ── */}
      <article className="py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="bg-white rounded-2xl p-6 md:p-10 border border-gray-100 shadow-sm">
            {page.content ? (
              <div
                className="prose prose-lg max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-p:leading-loose prose-a:text-amber-600 prose-a:no-underline hover:prose-a:text-amber-700 prose-strong:text-gray-800 prose-img:rounded-xl"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-amber-50">
                  <i className="ri-file-text-line text-2xl text-amber-400" />
                </div>
                <p className="text-gray-400">暂无内容</p>
              </div>
            )}
          </div>

          {/* 返回首页按钮 */}
          <div className="mt-8 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-amber-600 transition-colors group"
            >
              <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform" />
              返回首页
            </Link>
          </div>
        </div>
      </article>

      <SunsetFooter />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-[#fffbf5]">
      <Navbar />
      <section className="bg-gradient-to-br from-amber-100 via-orange-50 to-amber-50 pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center animate-pulse">
          <div className="w-48 h-8 bg-white/60 rounded-lg mx-auto" />
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="w-8 h-0.5 bg-white/40 rounded-full" />
            <div className="w-2 h-2 bg-white/40 rounded-full" />
            <div className="w-8 h-0.5 bg-white/40 rounded-full" />
          </div>
        </div>
      </section>
      <div className="py-12 max-w-3xl mx-auto px-6">
        <div className="animate-pulse space-y-4 p-10 bg-white rounded-2xl border border-gray-100">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`bg-gray-200 rounded ${i === 0 ? "h-7 w-2/3" : i === 5 ? "h-4 w-1/3" : "h-4 w-full"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-[#fffbf5] flex items-center justify-center">
      <div className="text-center px-4">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-amber-50">
          <i className="ri-file-damage-line text-3xl text-amber-400" />
        </div>
        <h1 className="text-xl font-bold text-gray-800 mb-2">页面未找到</h1>
        <p className="text-sm text-gray-500 mb-6">您访问的单页不存在或已被删除</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm font-medium rounded-xl hover:from-amber-600 hover:to-orange-700 transition-colors"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    </div>
  );
}
